import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { api } from '../../lib/api';

interface Endpoint {
  module: string;
  method: string;
  path: string;
}

/**
 * APIs > REST API docs (PC806). The reference links to a separate documentation site; here
 * the page lists the endpoints of this application's own API, read from the running server.
 */
export function ApiDocsPage() {
  const docs = useQuery({
    queryKey: ['api-docs'],
    queryFn: () => api.get<{ data: Endpoint[] }>('/api-docs'),
    select: (r) => r.data,
  });
  const modules = [...new Set((docs.data ?? []).map((e) => e.module))];

  return (
    <Box>
      <PageHeader title="REST API Docs" code="PC806" />
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, maxWidth: 960 }}>
        <Typography>
          Every address starts with <code>/api/v1</code> and answers in JSON. A request needs the
          session cookie of a signed-in user; a request that changes something also needs the header{' '}
          <code>X-CSRF-Token</code> with the value of the <code>csrf</code> cookie. Each endpoint
          checks the permission code of its screen and limits what it returns to the signed-in
          user&apos;s hierarchy. Errors come as{' '}
          <code>{'{ "error": { "code", "message", "details" } }'}</code>.
        </Typography>
      </Paper>
      {docs.error ? (
        <ErrorState error={docs.error} onRetry={() => void docs.refetch()} />
      ) : !docs.data ? (
        <LoadingState />
      ) : (
        modules.map((module) => (
          <Paper
            key={module}
            variant="outlined"
            component="section"
            aria-label={module}
            sx={{ mb: 1.5, maxWidth: 960 }}
          >
            <Typography variant="h3" component="h2" sx={{ px: 1.5, py: 1 }}>
              {module}
            </Typography>
            <Table size="small" aria-label={`${module} endpoints`}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 110 }}>Method</TableCell>
                  <TableCell>Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.data
                  .filter((e) => e.module === module)
                  .map((e) => (
                    <TableRow key={`${e.method} ${e.path}`}>
                      <TableCell>{e.method}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{e.path}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        ))
      )}
    </Box>
  );
}
